import { useTranslations } from "@fenetech/translations";
import { Autocomplete, Stack, TextField } from "@mui/material";
import useMfgCustomersAndSites from "helpers/context/SelectionValues/useMfgCustomersAndSites";
import { IMfgCustomer } from "models/IMfgCustomer";
import React, { useEffect, useMemo } from 'react';

interface IProps {
    isEditMode: boolean,
    selectedMfgCustomer: IMfgCustomer | null,
    selectedMfgSite: IMfgCustomer | null,
    setSelectedMfgCustomer: React.Dispatch<React.SetStateAction<IMfgCustomer | null>>,
    setSelectedMfgSite: (mfgSite: IMfgCustomer | null) => Promise<void>,
    authCustomerGUID: string | null
}

const ESignatureSelectCustomer: React.FC<IProps> = ({ isEditMode, selectedMfgCustomer, selectedMfgSite, setSelectedMfgCustomer, setSelectedMfgSite, authCustomerGUID }: IProps) => {

    const mfgCustomerSites = useMfgCustomersAndSites();
    const tm = useTranslations();

    const mfgCustomers = useMemo(() => {
        if (mfgCustomerSites) {
            return mfgCustomerSites.GetDistinctMfgCustomers();
        }
        return undefined;

    }, [mfgCustomerSites]);

    const mfgSites = useMemo(() => {
        if (mfgCustomerSites && selectedMfgCustomer) {
            return mfgCustomerSites.GetMfgSitesFromMfgCustomerID(selectedMfgCustomer.customerID);
        }
        return undefined;
    }, [mfgCustomerSites, selectedMfgCustomer]);

    useEffect(() => {
        if (mfgCustomers) {
            if (mfgCustomers.length === 1) {
                setSelectedMfgCustomer(mfgCustomers[0]);
            } else if (authCustomerGUID !== null) {
                //If we have an authCustomerGUID, we just came back from 3rd party sign in. Automatically select this customer
                const authorizedCustomer = mfgCustomers.find(c => c.customerGUID === authCustomerGUID);
                if (authorizedCustomer) {
                    setSelectedMfgCustomer(authorizedCustomer);
                }
            }
        }
    }, [mfgCustomers, setSelectedMfgCustomer, authCustomerGUID])

    useEffect(() => {
        if (mfgSites) {
            if (mfgSites.length === 1) {
                setSelectedMfgSite(mfgSites[0]);
            } else if (authCustomerGUID !== null) {
                //If we have an authCustomerGUID, we just came back from 3rd party sign in. Automatically select this site
                const authorizedSite = mfgSites.find(s => s.customerGUID === authCustomerGUID);
                if (authorizedSite) {
                    setSelectedMfgSite(authorizedSite);
                }
            }
        }
    }, [mfgSites, setSelectedMfgSite, authCustomerGUID])

    return <Stack direction="column" my={1} spacing={1}>
        <Autocomplete
            id="mfgCustomer"
            autoComplete
            autoSelect
            autoHighlight
            selectOnFocus
            handleHomeEndKeys
            options={mfgCustomers ?? []}
            getOptionLabel={(option) => (option?.name || "")}
            isOptionEqualToValue={(option, value) => (option?.name) === (value?.name)}
            disabled={!mfgCustomers || mfgCustomers.length <= 1 || isEditMode}
            renderInput={(params) => (
                <TextField
                    variant="outlined"
                    label={tm.Get("Mfg Customer")}
                    {...params}
                />
            )}
            value={selectedMfgCustomer}
            onChange={(_, value) => { setSelectedMfgCustomer(value); }}
        />

        <Autocomplete
            id="mfgSite"
            autoComplete
            autoSelect
            autoHighlight
            selectOnFocus
            handleHomeEndKeys
            options={mfgSites ?? []}
            getOptionLabel={(option) => (option?.siteName || "")}
            isOptionEqualToValue={(option, value) => (option?.siteName) === (value?.siteName)}
            disabled={!mfgSites || mfgSites.length <= 1 || isEditMode}
            renderInput={(params) => (
                <TextField
                    variant="outlined"
                    label={tm.Get("Mfg Site")}
                    {...params}
                />
            )}
            renderOption={(props, option) => (
                <li {...props} key={option.customerGUID}>
                    {option?.siteName || ""}
                </li>
            )}
            value={selectedMfgSite}
            onChange={(_, value) => { setSelectedMfgSite(value); }}
        />
    </Stack>;
}

export default ESignatureSelectCustomer;